import { useCallback, useMemo, useState } from 'react';
import filter from 'lodash/filter';
import size from 'lodash/size';
import take from 'lodash/take';

import {
  ChangeProductsFiltersFunc,
  FetchProductsFilterFields,
  FetchProductsFilters
} from '../../../../../../products/productsTypes';
import { CacheFetchProductsAggregationsQueryResponseAggregations } from '../../../../../../products/queries/fetchCacheProductsAggregations.query';
import { IconsEnum } from '../../../../../../../assets/icons/types';

import { ThreeDStockProductsFiltersCollectionsFilterItem } from './components/ThreeDStockProductsFiltersCollectionsFilterItem';

import { ButtonHelper } from '../../../../../../../helpers/buttons/ButtonHelper';
import { Icon } from '../../../../../../../helpers/Icon';
import { Translate } from '../../../../../../../helpers/Translate';
import { PureIconButtonHelper } from '../../../../../../../helpers/buttons/PureIconButtonHelper';

import { categoriesKeys, words } from '../../../../../../../locales/keys';

const INITIAL_TAGS_COUNT = 6;
const fieldName = FetchProductsFilterFields.COLLECTION_ID;

interface ThreeDStockProductsFiltersCollectionsFilterProps {
  productsAggregations: CacheFetchProductsAggregationsQueryResponseAggregations;
  productsFilters: FetchProductsFilters;
  changeProductsFilters: ChangeProductsFiltersFunc;
}

function ThreeDStockProductsFiltersCollectionsFilter({
  productsAggregations,
  changeProductsFilters,
  productsFilters
}: ThreeDStockProductsFiltersCollectionsFilterProps) {
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [minimized, setMinimized] = useState<boolean>(true);

  const toggleIsOpen = useCallback<() => void>(
    () => setIsOpen((prevState) => !prevState),
    []
  );

  const toggleMinimize = useCallback<() => void>(() => {
    setMinimized((prevState) => !prevState);
  }, [setMinimized]);

  const collections = productsAggregations?.collections;

  const checkedCollections: string[] = useMemo(
    () => productsFilters[fieldName]?.in || [],
    [productsFilters]
  );

  const handleCheckedCollection = useCallback(
    (collectionId: string, checked: boolean) => {
      const newCollections = checked
        ? [...checkedCollections, collectionId]
        : filter(
            checkedCollections,
            (collection) => collection !== collectionId
          );

      changeProductsFilters(
        {
          [fieldName]: {
            in: newCollections
          }
        },
        size(newCollections) > 0 ? [] : [fieldName]
      );
    },
    [checkedCollections, changeProductsFilters]
  );

  if (size(collections) === 0) {
    return null;
  }

  return (
    <div className="p-4">
      <div className="flex flex-col gap-3">
        <ButtonHelper
          className="flex items-center gap-2 rounded-sm"
          dataGa="collections_filter"
          onClick={toggleIsOpen}
        >
          <span className="text-sm font-semibold text-left flex-1">
            <Translate id={categoriesKeys.collections.plural} />
          </span>

          <Icon
            icon={isOpen ? IconsEnum.CHEVRON_DOWN_SOLID : IconsEnum.CHEVRON_UP}
            className="h-5 w-5 text-gray-500"
          />
        </ButtonHelper>

        {isOpen && (
          <ul>
            {(minimized
              ? take(collections, INITIAL_TAGS_COUNT)
              : collections
            )?.map((collection, index) => (
              <ThreeDStockProductsFiltersCollectionsFilterItem
                key={index}
                checked={checkedCollections?.includes(collection.id)}
                onCheckedCollection={handleCheckedCollection}
                collection={collection}
              />
            ))}

            {size(collections) > INITIAL_TAGS_COUNT && (
              <PureIconButtonHelper
                className="p-0 space-x-1 flex gap-1 text-gray-900 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-100 leading-5 focus:ring-base text-current hover:underline transition-colors duration-150 ease-out"
                i18nText={minimized ? words.showAll : words.showLess}
                i18nTextClassName="text-sm underline"
                onClick={toggleMinimize}
                icon={
                  minimized
                    ? IconsEnum.CHEVRON_DOWN_SOLID
                    : IconsEnum.CHEVRON_UP
                }
                iconClassName="w-5 h-5"
              />
            )}
          </ul>
        )}
      </div>
    </div>
  );
}

export default ThreeDStockProductsFiltersCollectionsFilter;
